import React, { useEffect, useState, useMemo } from 'react';
import DashboardAnalytics from '../components/DashboardAnalytics';
import { fetchInvoices } from '../services/InvoiceService';
import { fetchExpenses } from '../services/BillsExpensesService';
import { useAuth } from '../../AuthProvider';
import GstPayGSummary from './accounting/GstPayGSummary';

// Helper function to calculate Financial Year (e.g., "FY 2025-2026")
// Assumes July 1st start. Change `(month >= 6)` to `(month >= 0)` if using Calendar Year instead.
const getFinancialYear = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (June is 5, July is 6)

  if (month >= 6) {
    return `FY ${year}-${year + 1}`;
  } else {
    return `FY ${year - 1}-${year}`;
  }
};

const DashboardPage = () => {
  const auth = useAuth();
  
  // Raw data states from API
  const [allInvoices, setAllInvoices] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  
  // Filter state
  const [selectedFY, setSelectedFY] = useState('All');

  useEffect(() => {
    async function load() {
      try {
        const invRes = await fetchInvoices(auth.token);
        const expRes = await fetchExpenses(auth.token,auth.additionalUserInfo.companyId);

        // Sort utility
        const sortByDateDesc = (arr) =>
          [...arr].sort((a, b) => {
            const da = new Date(a.dated || a.createdDate || a.date || 0);
            const db = new Date(b.dated || b.createdDate || b.date || 0);
            return db - da;
          });

        setAllInvoices(sortByDateDesc(invRes.data || []));
        setAllExpenses(sortByDateDesc(expRes.data || []));
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      }
    }
    load();
  }, [auth.token]);

  // 1. Dynamically extract all available Financial Years from both datasets
  const fyOptions = useMemo(() => {
    const years = new Set();
    
    allInvoices.forEach(i => {
      const fy = getFinancialYear(i.dated || i.createdDate || i.date);
      if (fy) years.add(fy);
    });

    allExpenses.forEach(e => {
      const fy = getFinancialYear(e.dated || e.createdDate || e.date);
      if (fy) years.add(fy);
    });

    // Sort descending so newest FY appears first
    return ['All', ...Array.from(years).sort().reverse()];
  }, [allInvoices, allExpenses]);

  // 2. Filter data based on selected FY
  const filteredData = useMemo(() => {
    let invoices = allInvoices;
    let expenses = allExpenses;

    if (selectedFY !== 'All') {
      invoices = allInvoices.filter(i => getFinancialYear(i.dated || i.createdDate || i.date) === selectedFY);
      expenses = allExpenses.filter(e => getFinancialYear(e.dated || e.createdDate || e.date) === selectedFY);
    }

    // Calculate totals for the filtered subset
    const totalIncome = invoices.reduce((sum, i) => sum + (parseFloat(i.grandTotal) || parseFloat(i.amount) || 0), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.claimedAmount) || parseFloat(e.amount) || 0), 0);

    return {
      income: totalIncome,
      expenses: totalExpenses,
      recentInvoices: invoices.slice(0, 5),
      recentExpenses: expenses.slice(0, 5)
    };
  }, [selectedFY, allInvoices, allExpenses]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '20px' }}>
        <h2></h2>
        
        {/* FY Dropdown Selector */}
        <div>
          <label htmlFor="fy-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>Filter by Financial Year: </label>
          <select 
            id="fy-select" 
            value={selectedFY} 
            onChange={(e) => setSelectedFY(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            {fyOptions.map(fy => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </div>
      </div>

      <DashboardAnalytics 
        income={filteredData.income} 
        expenses={filteredData.expenses} 
        profit={filteredData.income - filteredData.expenses} 
        recentInvoices={filteredData.recentInvoices} 
        recentExpenses={filteredData.recentExpenses} 
      />
      
      {/* Passing selectedFY down here just in case GstPayGSummary needs to filter its data too */}
      <GstPayGSummary 
        companyId={auth.additionalUserInfo?.companyId} 
        token={auth.token} 
        selectedFY={selectedFY}
      />
    </div>
  );
};

export default DashboardPage;